import { FormRowStatus } from 'atoms/create-resource-atom';
import searchAtomPair from 'atoms/search-atom';
import { ComboBox, ComboBoxProps, Modal, Layer } from '@carbon/react';
import { EntityForm } from 'components/entity-form';
import { useAtom, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EntityResponse } from 'types/api-responses';
import { shouldSkipSearch } from 'utils/forms';
import style from './entity-autocomplete-with-creation-modal.module.scss';

type Props = Omit<
  ComboBoxProps<EntityResponse>,
  'items' | 'light' | 'onChange'
> & {
  onCreationSuccess?: (entity: EntityResponse) => void;
  onChangeSuccess: (entity: EntityResponse | null | undefined) => void;
  status: FormRowStatus;
  hideCreateOption?: boolean;
  searchPayloadOverrides?: Record<string, unknown>;
  formatEntityLabel?: (entity: EntityResponse) => string;
  formatEntityElement?: (entity: EntityResponse) => React.ReactNode;
  light?: boolean;
};

const EntityCreationModal = ({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (entity: EntityResponse) => void;
}) => {
  return (
    <Modal
      open={isOpen}
      size="lg"
      modalHeading="Entity Creation"
      className={style.entityCreationModal}
      passiveModal
      onRequestClose={onClose}
    >
      {isOpen && (
        <EntityForm
          entity={undefined}
          loading={false}
          getEntity={undefined}
          onSuccess={onSuccess}
          showCancel={false}
        />
      )}
    </Modal>
  );
};

const EntityAutocompleteWithCreationModal = ({
  onCreationSuccess,
  onChangeSuccess,
  status,
  hideCreateOption = false,
  searchPayloadOverrides,
  formatEntityLabel,
  formatEntityElement,
  light = false,
  ...rest
}: Props) => {
  const [openEntityModal, setOpenEntityModal] = useState(false);
  const { searchAtom, resetSearchDataAtom } = useMemo(
    () => searchAtomPair<EntityResponse>('entities'),
    []
  );

  const [searchResults, searchPayload] = useAtom(searchAtom);
  const resetSearchData = useSetAtom(resetSearchDataAtom);

  const debouncedSearchPayload = useMemo(
    () => debounce(searchPayload, 400),
    [searchPayload]
  );

  const firstRender = useRef(true);
  useEffect(() => {
    firstRender.current = false;
  }, []);

  const results = searchResults?.data?.results || [];
  const items = hideCreateOption
    ? results
    : results.concat([{ id: -1 } as EntityResponse]);

  const comboBox = (
    <ComboBox
      {...rest}
      items={items}
      placeholder=""
      itemToString={(data) => {
        if (!data || data.id === -1) return '';
        return formatEntityLabel
          ? formatEntityLabel(data)
          : `${data.name}, ${data.id}`;
      }}
      itemToElement={(item) => {
        if (item.id === -1) {
          return <strong>Add New Entity</strong>;
        }
        if (formatEntityElement) {
          return <>{formatEntityElement(item)}</>;
        }
        return (
          <>
            {item.name}, {item.id}
          </>
        );
      }}
      onChange={({ selectedItem }) => {
        if (selectedItem && selectedItem.id === -1) {
          setOpenEntityModal(true);
          return;
        }
        onChangeSuccess(selectedItem);
      }}
      onInputChange={(text) => {
        if (shouldSkipSearch(firstRender.current, status)) return;
        resetSearchData(undefined);
        debouncedSearchPayload((current) => ({
          ...current,
          text,
          pageSize: 10,
          ...searchPayloadOverrides,
        }));
      }}
    />
  );

  return (
    <>
      {!hideCreateOption && onCreationSuccess && (
        <EntityCreationModal
          isOpen={openEntityModal}
          onClose={() => {
            setOpenEntityModal(false);
          }}
          onSuccess={(entity) => {
            onCreationSuccess(entity);
            setOpenEntityModal(false);
          }}
        />
      )}
      {light ? <Layer>{comboBox}</Layer> : comboBox}
    </>
  );
};

export { EntityAutocompleteWithCreationModal };
